"use client"
import { motion, useScroll, useSpring, useTransform } from "motion/react"
import { RefObject, useEffect, useRef, useState } from "react"

interface CanvasRendererProps {
  frameIndex: number
}

const frames = Array.from({ length: 30 }, (_, i) => `/marketing/case-frames-1080/frame-${i.toString()}.webp`)

const CanvasRenderer = ({ frameIndex }: CanvasRendererProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [loadedImages, setLoadedImages] = useState<Record<number, HTMLImageElement>>({})
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    // Load initial frame and next few frames
    const initialFramesToLoad = [frameIndex, (frameIndex + 1) % frames.length, (frameIndex + 2) % frames.length]
    setIsLoading(true)

    Promise.all(
      initialFramesToLoad.map(
        index =>
          new Promise<[number, HTMLImageElement]>(resolve => {
            const img = new Image()
            img.onload = () => resolve([index, img])
            img.src = frames[index]
          })
      )
    ).then(imageEntries => {
      setLoadedImages(prev => ({
        ...prev,
        ...Object.fromEntries(imageEntries),
      }))
      setIsLoading(false)
    })
  }, [])

  // Preload next frames as user scrolls
  useEffect(() => {
    if (!loadedImages[frameIndex]) {
      const img = new Image()
      img.onload = () => {
        setLoadedImages(prev => ({
          ...prev,
          [frameIndex]: img,
        }))
      }
      img.src = frames[frameIndex]
    }

    // Preload next frame
    const nextIndex = (frameIndex + 1) % frames.length
    if (!loadedImages[nextIndex]) {
      const img = new Image()
      img.onload = () => {
        setLoadedImages(prev => ({
          ...prev,
          [nextIndex]: img,
        }))
      }
      img.src = frames[nextIndex]
    }
  }, [frameIndex, loadedImages])

  useEffect(() => {
    if (!canvasRef.current || !loadedImages[frameIndex]) return
    const ctx = canvasRef.current.getContext("2d")
    if (!ctx) return

    ctx.clearRect(0, 0, 840, 840)
    ctx.drawImage(loadedImages[frameIndex], 0, 0, 840, 840)
  }, [frameIndex, loadedImages])

  if (isLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <div className="h-32 w-32 animate-pulse rounded-full" />
      </div>
    )
  }

  return <canvas ref={canvasRef} width={840} height={840} className="h-full w-full" />
}

export const ScrollControlledAnimation = () => {
  const [isMobile, setIsMobile] = useState(false)
  useEffect(() => {
    setIsMobile(window.innerWidth < 900)
  }, [setIsMobile])

  const containerRef = useRef<HTMLDivElement>(null)
  const [frameIndex, setFrameIndex] = useState(0)

  const { scrollYProgress } = useScroll({
    target: containerRef as RefObject<HTMLDivElement>,
    offset: ["start end", "end start"],
  })

  // Add smooth spring physics
  const smoothProgress = useSpring(scrollYProgress, {
    damping: 50,
    stiffness: 400,
    mass: 0.5,
  })

  const currentFrame = useTransform(smoothProgress, [0.45, 0.65], [0, frames.length - 1], {
    clamp: false,
  })

  const yOffset = useTransform(smoothProgress, [0.45, 0.65], [isMobile ? 150 : 300, isMobile ? 125 : 250], {
    clamp: true,
  })

  useEffect(() => {
    const unsubscribe = currentFrame.on("change", latest => {
      requestAnimationFrame(() => {
        const frame = Math.round(Math.min(Math.max(latest, 0), frames.length - 1))
        setFrameIndex(frame)
      })
    })
    return () => unsubscribe()
  }, [currentFrame])

  return (
    <motion.div
      style={{
        translateY: yOffset,
      }}
      ref={containerRef}
      className="relative z-[-5] mt-[-200px] w-full md:mt-[-400px]">
      <div className="sticky top-0 flex w-full items-center justify-center">
        <div className="relative size-[330px] md:size-[630px] lg:size-[630px]">
          <CanvasRenderer frameIndex={frameIndex} />
        </div>
      </div>
    </motion.div>
  )
}
